import { useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { User } from '../../../../../services/endpoints/people/user';
import { useGetAllSnippetsQuery } from '../../../../../services/endpoints/website/snippet';
import { getCurrentUser } from '../../../../../services/helper';
import { Snippet } from '../../../../../types/website.types';
import { snippetColumns } from '../../../../../utils/consts/columnDefs/websiteColumns';
import { getCreatedUpdatedAtFromTo } from '../../../../../utils/consts/filterFields/dates';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';

const defaultScope = '';
const currentUser = getCurrentUser();

export const userCanEditCreateDelete_Snippets = (user: User | null) => {
  if (user?.isSuper || user?.isSupremeAdmin || user?.isWebContentEditor) {
    return true;
  }

  return false;
};

export const userCanView_Snippets = (user: User | null) => {
  if (user?.isSuper || user?.isSupremeAdmin || user?.isWebContentEditor || user?.isGlobal) {
    return true;
  }

  return false;
};

export const SnippetsView = () => {
  const { addToast } = useToast();
  const [filters, setFilters] = useListFiltersInUrl({
    scope: defaultScope,
    data: { page: 1, entries: 10 }
  });
  const { data, error, isLoading, isFetching } = useGetAllSnippetsQuery(filters);
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const navigate: NavigateFunction = useNavigate();
  const isGlobalUser = currentUser?.isGlobal;

  const filterFormFields: FilterField[] = [
    {
      name: 'name',
      label: 'Name',
      value: getFilterValue(filters.data?.filterBy, 'name')
    },
    {
      name: 'content',
      label: 'Content',
      value: getFilterValue(filters.data?.filterBy, 'content')
    },
    ...getCreatedUpdatedAtFromTo(filters.data?.filterBy)
  ];

  if (!isGlobalUser) {
    filterFormFields.splice(4, 1);
  }

  useEffect(() => {
    if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setSnippets(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  return (
    <div className="py-2">
      <SFCTable
        name={`Snippets`}
        columns={snippetColumns}
        data={snippets}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        isLoading={isLoading}
        isFetching={isFetching}
        indexDownloadPath="snippets"
        filters={filters?.data?.filterBy}
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        newItemEl={
          userCanEditCreateDelete_Snippets(currentUser) && (
            <SimpleButton
              variant="sfc-blueberry"
              height={40}
              truncate={false}
              onClick={() => navigate('/admin/website/snippets/new')}
            >
              New Snippet
            </SimpleButton>
          )
        }
        onFiltersChanged={setFilters}
      />
    </div>
  );
};
